/**
 * useNotifications — one bell, fed by every channel Karr has, in real time.
 *
 * ## The delivery contract (2026-07-27)
 *
 * A notification must reach you **without a reload**, the way email and WhatsApp
 * do. Getting that right is not about subscribing to a socket — it is about never
 * trusting one. This hook now guarantees delivery through four independent paths,
 * any ONE of which is enough:
 *
 *   1. **Realtime** — the fast path, sub-second, via `useLiveChannel`, which
 *      re-subscribes itself with backoff when the socket dies instead of going
 *      quiet forever (the old `.subscribe()` was a one-shot hope).
 *   2. **Adaptive poll** — every 90s while the socket is healthy, every 8s while
 *      it is NOT. So a dead socket costs seconds of lateness, never a reload.
 *   3. **Wake** — tab focus, tab visible, and network back online all refetch
 *      immediately. Returning to Karr is always instantly correct.
 *   4. **Re-subscribe catch-up** — anything that changed while the socket was
 *      down was never delivered, so a successful re-subscribe refetches too.
 *
 * ## The alert is decided in ONE place
 *
 * The chime used to live inside the socket handlers, so an item delivered by a
 * poll or a refetch arrived **silently** — the exact failure the 2026-07-20 GST
 * packet incident produced, fixed then for two feeds only. Now every feed flows
 * into one effect that diffs the merged list: an unread item whose id has not
 * been seen before rings once, toasts once, and — when Karr is not the window in
 * front — raises one OS notification. Whichever transport delivered it.
 *
 * ## One engine, not one per component
 *
 * `/notifications` renders inside `AppLayout`, so the old hook ran TWICE, opening
 * two channels on the same topic and doubling every fetch and every chime.
 * `useNotificationEngine()` is mounted once (AppLayout); `useNotifications()` is
 * now a pure reader of the shared store and safe to call anywhere.
 *
 * ## Feeds
 *
 * ORG-scoped (active org): `ca_notifications`, `document_requests`, client
 * queries, challan tasks, HR reminders.
 * USER-scoped (cross-org, independent of the active org): referral signups,
 * incoming books-access requests, incoming/accepted team invitations, member
 * access-expiry alerts. Gating these on the active org would hide them.
 *
 * "Read" state: `ca_notifications` (read_at) and referrals (acknowledged_at)
 * persist server-side. The derived feeds have no read column, so dismissals live
 * in `notification_dismissals` and are anti-joined out server-side — EXCEPT
 * sticky items (pending invitations): those are open work, immune to every
 * dismissal path, gone only when accepted/cancelled/expired (20260806101429).
 *
 * @since 3.4.0
 */
import { useEffect, useCallback, useRef, useMemo } from 'react';
import { create } from 'zustand';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@/shared/ui/toast';
import { supabase } from '@/db/supabase';
import { useAuth } from './useAuth';
// seatsChanged rides a chunk of its own: the shell loads this hook on every
// first visit, and the first-load budget had 0.1 KB to spare (9 Oct 2026).
const seatsChanged = () => { void import('@/shared/team/seatsChanged').then((m) => m.seatsChanged()); };
import { isAlertPopupsMuted } from '@/shared/utils/soundPrefs';
import { showDesktopNotification, windowIsForeground } from '@/shared/utils/desktopNotify';
import { pushOnHere, setIconCount, syncPushSubscription } from '@/shared/push/webPush';
import {
    useLiveChannel, useLiveHealthStore, realtimeHealth, realtimePublishedTables,
} from '@/shared/utils/liveChannel';
import { formatDate } from '@/shared/utils/formatters';
import { ROLE_LABELS } from '@/shared/lib/capabilities';
import type { Notification, NotificationType } from '@/shared/components/NotificationCenter';

interface DBNotification {
    id: string;
    type: string;
    subject: string;
    body: string;
    priority: string;
    status: string;
    created_at: string;
    read_at: string | null;
    practice_id: string;
    client_id: string;
}

interface DBDocRequest {
    id: string;
    org_id: string;
    title: string;
    status: string;
    requested_by: string | null;
    created_at: string;
}

// Map DB notification types to UI types
function mapType(dbType: string): NotificationType {
    switch (dbType) {
        case 'compliance_alert': return 'warning';
        case 'payment_reminder': return 'alert';
        case 'status_update': return 'success';
        case 'doc_request':
        case 'general':
        default: return 'info';
    }
}

function dbToNotification(row: DBNotification): Notification {
    return {
        id: row.id,
        type: mapType(row.type),
        title: row.subject,
        description: row.body?.slice(0, 120) || '',
        timestamp: new Date(row.created_at),
        read: !!row.read_at,
        actionHref: '/practice-crm',
    };
}

// ── of-3: the per-PERSON notification (`notifications`, 00001) ──────────
// org + user + type + event_key. Written by five senders since 00146 and read
// by nothing until now. `get_my_notifications` scopes it to the caller.
export interface DBMyNotification {
    id: string;
    type: string;
    title: string;
    message: string | null;
    event_key: string | null;
    related_type: string | null;
    related_id: string | null;
    is_read: boolean;
    org_id: string;
    org_name: string | null;
    created_at: string;
}

const MINE_PREFIX = 'mine:';

/** Work needing attention reads as a call to act; a stage moving is just news. */
function mapMyType(dbType: string): NotificationType {
    switch (dbType) {
        case 'compliance_block':
        case 'payment_overdue':
        case 'copilot_update':
            return 'alert';
        case 'compliance_warning':
        case 'payment_due':
        case 'task_due':
        case 'task_assigned':
        case 'task_delegated':
        case 'approval_request':
            return 'warning';
        case 'approval_resolved':
        case 'report_ready':
            return 'success';
        default:
            return 'info';
    }
}

/** Where a notification's `related_type` says the work actually lives. Only
 *  destinations that exist: a row that lands somewhere wrong is worse than a row
 *  that just tells you, because you now believe you have been taken to it. */
const MY_NOTIF_HREF: Record<string, string> = {
    // gridsees: a task row opens on ITS row (`myNotifHref` adds `?task=<id>`).
    task: '/my-work',
    // The consultant's evening round names clients; the cockpit is the screen
    // with one row per client, which is where "3 clients need a look" resolves.
    agent_round: '/portfolio',
    // copilotupd: "Karr Copilot x.y.z is out" lands on the tab with the Update button.
    copilot: '/settings#copilot',
    // fixrcm: "October's fixed expenses: still the same?" is answered on the home (Flow)
    // or in the fill rail on the same page (Books); a fixed expense Karr could not book
    // is fixed from the register.
    fixed_expenses: '/dashboard',
    // A scheduled supplier bill or invoice that did not post lives on its own page.
    recurring: '/recurring-bills',
    // teamscope: "You look after X now" lands on the Board, which now holds X.
    office_client: '/portfolio',
    // teamscope: "Your team: 12 open tasks" lands on My Work's team card.
    team: '/my-work#team',
    // zeropay zp-8: the payroll clock's notice (ready to approve, upload the file) lands on the month.
    payroll_run: '/payroll',
    // officewatch: a failed night, an engine only watching, work with nobody on it:
    // all three are read and fixed from the Day Book.
    office_watch: '/portfolio',
    // officetds ot-5: "TDS Q2: 3 clients blocked on us …" opens Office > TDS with that count chosen.
    office_tds: '/office-tds?show=us',
    // flagfix: "Karr gave you N tasks nobody handles" opens the work itself.
    office_fallback: '/my-work',
    // fl-12: "Priya has nothing to do today, Rahul at 118% this week" opens the
    // Floor, where work is handed out and moved.
    office_floor: '/portfolio#floor',
    // pm-17: "Time to revise: GST on bills" opens the Accounting Score, where it waits under Time to revise.
    concept: '/learn',
};

/**
 * Where one of my notifications lands. A task row lands ON the task: My Work
 * scrolls to it and rings it (gridsees), so "This is now with you" is one tap
 * from the work. "Nobody on your team handles X" is about a kind, not a task,
 * and lands on the Team tab finding a person for it.
 */
export function myNotifHref(row: Pick<DBMyNotification, 'related_type' | 'related_id' | 'event_key' | 'title'>): string | undefined {
    if (!row.related_type) return undefined;
    if (row.related_type === 'task') {
        const kind = row.event_key === 'task.nobody_handles'
            ? /^Nobody on your team handles (.+)$/.exec(row.title ?? '')?.[1]
            : undefined;
        if (kind) return `/portfolio#team?kind=${encodeURIComponent(kind)}`;
        return row.related_id ? `/my-work?task=${encodeURIComponent(row.related_id)}` : '/my-work';
    }
    // teamscope: a client's WhatsApp (or a chat handed to you) opens that chat
    // in the Karr Phone, which reads `?wa=` on whatever page it lands.
    if (row.related_type === 'wa_thread') {
        return row.related_id ? `/my-work?wa=${encodeURIComponent(row.related_id)}` : '/my-work';
    }
    // improve: the Karr team's answer opens Improve Karr on that idea (the shell
    // reads ?idea= on any page), over the Alerts list it came from. Karr's own
    // row about a new idea opens it in the inbox.
    if (row.related_type === 'karr_idea') {
        return row.related_id ? `/notifications?idea=${encodeURIComponent(row.related_id)}` : '/notifications';
    }
    if (row.related_type === 'karr_idea_admin') {
        return row.related_id ? `/admin/ideas?idea=${encodeURIComponent(row.related_id)}` : '/admin/ideas';
    }
    // meetattend: "Mehvish Lone will attend" opens that meeting's full view on ROC Compliance.
    if (row.related_type === 'company_meeting') {
        return row.related_id ? `/roc?tab=meetings&meeting=${encodeURIComponent(row.related_id)}` : '/roc?tab=meetings';
    }
    // practice-21: a new order on a matter opens that matter's orders.
    if (row.related_type === 'practice_matter') {
        return row.related_id ? `/practice/matters?tab=orders&matter=${encodeURIComponent(row.related_id)}` : '/practice/matters?tab=orders';
    }
    // practice-23: a watch's hit (an appeal that may have been filed, a look-alike mark, a watched word) opens the hit itself.
    if (row.related_type === 'practice_watch_hit') {
        return row.related_id ? `/practice/matters?tab=matters&hit=${encodeURIComponent(row.related_id)}` : '/practice/matters?tab=matters';
    }
    // proposalux: a client's answer to a proposal lands on their card in Clients.
    if (row.related_type === 'practice_client') {
        return row.related_id ? `/practice-crm?client=${encodeURIComponent(row.related_id)}` : '/practice-crm';
    }
    // ts-8: a broken promise or a follow-up lands on the trip itself.
    if (row.related_type === 'travel_trip') {
        return row.related_id ? `/travel/trip/${encodeURIComponent(row.related_id)}` : '/travel';
    }
    return MY_NOTIF_HREF[row.related_type];
}

/** improve: a row about Karr itself (an idea) is about no business, so a tap stays in the one you are in. */
const ABOUT_KARR = new Set(['karr_idea', 'karr_idea_admin', 'concept']);

/** Whether a tap first stands in the row's business. */
export function myNotifStandsIn(row: Pick<DBMyNotification, 'related_type' | 'event_key'>): boolean {
    if (ABOUT_KARR.has(row.related_type ?? '')) return false;
    return row.related_type !== 'task' || row.event_key === 'task.nobody_handles';
}

function myNotifToNotification(row: DBMyNotification): Notification {
    return {
        id: `${MINE_PREFIX}${row.id}`,
        type: mapMyType(row.type),
        title: row.title,
        // The org name goes FIRST, not last. This mail is cross-org: a
        // consultant's list holds rows from every business he touches, several
        // may share a title ("Today's books"), and WHICH ONE is the word he needs
        // before any number in the body. It used to be appended after the
        // message, where a 160-char cut could drop it entirely — so two alerts
        // read identically and only the third line, if it survived, told them
        // apart. The cap is generous now because a digest that names five clients
        // is five short lines, and truncating it mid-client hides a client.
        description: [row.org_name, row.message].filter(Boolean).join(' · ').slice(0, 420),
        timestamp: new Date(row.created_at),
        read: !!row.is_read,
        actionHref: myNotifHref(row),
        // phonealerts: one card per event in the phone's Alerts, and a tap
        // lands in the business it is about. A task row is the exception:
        // My Work is the office's desk across every client, so standing in
        // the client first would take the desk away.
        groupKey: row.event_key ? `${MINE_PREFIX}${row.event_key}` : undefined,
        orgId: row.org_id,
        orgName: row.org_name ?? undefined,
        message: row.message ?? undefined,
        standIn: myNotifStandsIn(row),
    };
}

// A client pressed "something's wrong" on a review/approval link — the one
// notification with a human waiting on the other side. Links straight to the
// GST cockpit with the GSTIN + period pre-selected.
export interface DBClientQuery {
    id: string;
    gstin: string | null;
    form: string | null;
    period_key: string | null;   // 'MMYYYY'
    query_comment: string | null;
    queried_at: string;
}
const CQ_PREFIX = 'cq:';
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function periodWords(period: string | null): string {
    if (!period || !/^\d{6}$/.test(period)) return '';
    const m = Number(period.slice(0, 2));
    return m >= 1 && m <= 12 ? `${MONTH_NAMES[m - 1]} ${period.slice(2)}` : '';
}
function clientQueryToNotification(row: DBClientQuery): Notification {
    const ctx = [row.form, periodWords(row.period_key)].filter(Boolean).join(' · ');
    return {
        id: `${CQ_PREFIX}${row.id}`,
        type: 'warning',
        title: `Your client wants to talk${ctx ? ` — ${ctx}` : ''}`,
        description: row.query_comment ? `“${row.query_comment.slice(0, 110)}”` : 'They pressed "something\'s wrong" without a comment — call them.',
        timestamp: new Date(row.queried_at),
        read: false,
        actionHref: `/gst-filing${row.gstin && row.period_key ? `?gstin=${encodeURIComponent(row.gstin)}&period=${row.period_key}` : ''}`,
    };
}

// A cash-due return is parked on `awaiting_payment` — the practice's cue to create the
// PMT-06 challan (sf-10). Derived from those packets; links to the GST cockpit with the
// GSTIN + period pre-selected, where the mirror card is. Persists until the money lands
// (the packet leaves awaiting_payment) — a tap must NOT dismiss it (real ₹ still owed).
export interface DBChallanTask {
    id: string;
    gstin: string | null;
    form: string | null;
    period_key: string | null;   // 'MMYYYY'
    approved_at: string | null;
    sent_at: string | null;
    snapshot: {
        orgName?: string; formLabel?: string; periodLabel?: string;
        headline?: { amount?: number } | null;
        cashLeg?: { payable?: number } | null;
    } | null;
}
const CH_PREFIX = 'ch:';
export function challanTaskToNotification(row: DBChallanTask): Notification {
    const snap = row.snapshot ?? {};
    const who = (snap.orgName ?? '').trim();
    const formLabel = snap.formLabel || row.form || 'GST return';
    const period = snap.periodLabel || periodWords(row.period_key) || '';
    // The cash still owed — whole rupees, the number they'll type into the challan.
    const amount = Math.round(Number(snap.headline?.amount ?? snap.cashLeg?.payable ?? 0));
    const amountStr = amount > 0 ? `₹${amount.toLocaleString('en-IN')}` : '';
    // Say exactly whose challan, for what, and how much — never the old false "client
    // approved" line (a cash-due 3B is deferred; the client never approved — they
    // authorise AFTER paying, docs/CLIENT_SELF_FILING.md Sec. 15).
    return {
        id: `${CH_PREFIX}${row.id}`,
        type: 'warning',
        title: `Create the challan${who ? ` — ${who}` : ''}`,
        description: `${[formLabel, period].filter(Boolean).join(' ')}${amountStr ? ` needs ${amountStr} deposited` : ' has tax to deposit'} before it can be filed. Make the PMT-06 challan on the portal (the grid is ready in the GST cockpit), then share it with the client.`,
        timestamp: new Date(row.approved_at ?? row.sent_at ?? Date.now()),
        read: false,
        actionHref: `/gst-filing${row.gstin && row.period_key ? `?gstin=${encodeURIComponent(row.gstin)}&period=${row.period_key}` : ''}`,
    };
}

// Pending document requests appear as unread 'info' notes that link to /requests.
const DR_PREFIX = 'dr:';
function docReqToNotification(row: DBDocRequest): Notification {
    return {
        id: `${DR_PREFIX}${row.id}`,
        type: 'info',
        title: 'Document requested',
        description: row.title || 'Your accountant requested a document',
        timestamp: new Date(row.created_at),
        read: false,
        actionHref: '/requests',
    };
}

// Referral events — USER-level (cross-org), fetched via a definer RPC. `kind`
// distinguishes an early "signed up via your link" lead (pre-org) from a
// "created their business" referral.
interface DBPartnerNotif {
    id: string;
    kind?: string;
    org_name: string | null;
    email: string | null;
    status: string;
    created_at: string;
    is_new: boolean;
}
const REF_PREFIX = 'ref:';
function refToNotification(row: DBPartnerNotif): Notification {
    const who = row.email || row.org_name || 'Someone';
    if (row.kind === 'signup') {
        // Fires the moment they sign up — before they finish setting up the business.
        return {
            id: `${REF_PREFIX}${row.id}`,
            type: 'success',
            title: 'New signup via your link',
            description: `${who} just signed up — reach out to help them get set up`,
            timestamp: new Date(row.created_at),
            read: !row.is_new,
            actionHref: '/partner',
        };
    }
    return {
        id: `${REF_PREFIX}${row.id}`,
        type: 'success',
        title: 'New referral signup',
        description: `${who} signed up via your link${row.status === 'pending' ? ' — not paid yet' : ''}`,
        timestamp: new Date(row.created_at),
        read: !row.is_new,
        actionHref: '/partner',
    };
}

// Incoming team invitations addressed to ME (I'm an existing Karr user) — so an
// invite lands in the bell instead of an emailed link.
//
// A PENDING invitation is a work item, not a notice: it is `sticky` (cannot be
// dismissed, tapped away, or swept by "mark all read") and stays until it is
// accepted, cancelled by the sender, or expires. Clients invited their
// consultant and the consultant saw nothing — one tap had written a permanent
// dismissal. `status`/`expires_at` arrive with migration 20260806101429; until
// the founder applies it the rows simply come back without them and every
// invitation reads as pending, which errs on the visible side.
export interface IncomingInvite {
    id: string;
    token: string;
    role: string;
    org_name: string;
    inviter_name: string | null;
    created_at: string;
    /** Effective status — 'expired' means "ask the sender to re-send". */
    status?: 'pending' | 'expired';
    expires_at?: string;
}
const INV_PREFIX = 'inv:';
/**
 * The plain name an invitation shows for a role. Re-exported from the one role
 * list rather than kept as a second copy: this map used to say `viewer:
 * 'Auditor'`, which aud-1 made untrue the moment `auditor` became its own role —
 * an invitation to read-only access would have announced itself as an audit
 * appointment.
 */
export const INV_ROLE_LABEL: Record<string, string> = ROLE_LABELS;
function inviteToNotification(row: IncomingInvite): Notification {
    const who = row.inviter_name || 'Someone';
    const asRole = INV_ROLE_LABEL[row.role] || row.role;
    if (row.status === 'expired') {
        // Not actionable by the invitee, so it arrives already read (no badge)
        // and stays dismissable — but it must EXIST, or "they invited me last
        // month and I see nothing" has no answer on any screen.
        return {
            id: `${INV_PREFIX}${row.token}`,
            type: 'warning',
            title: `Invitation to ${row.org_name} expired`,
            description: `${who}'s invitation to join as ${asRole} expired${row.expires_at ? ` on ${formatDate(row.expires_at)}` : ''} — ask them to send it again.`,
            timestamp: new Date(row.created_at),
            read: true,
            actionHref: `/invite/${row.token}`,
        };
    }
    return {
        id: `${INV_PREFIX}${row.token}`,
        type: 'info',
        title: `Invitation to join ${row.org_name}`,
        description: `${who} invited you to join as ${asRole} — tap to accept`,
        timestamp: new Date(row.created_at),
        read: false,
        actionHref: `/invite/${row.token}`,
        sticky: true,
    };
}

// An invitation I sent was ACCEPTED — shown to the org's owner/admins.
interface DBAcceptedInvite {
    id: string;
    email: string;
    role: string;
    org_name: string;
    accepted_at: string;
}
const IACC_PREFIX = 'iacc:';
function acceptedInviteToNotification(row: DBAcceptedInvite): Notification {
    const asRole = INV_ROLE_LABEL[row.role] || row.role;
    return {
        id: `${IACC_PREFIX}${row.id}`,
        type: 'success',
        title: 'Invitation accepted',
        description: `${row.email} joined ${row.org_name} as ${asRole}`,
        timestamp: new Date(row.accepted_at),
        read: false,
        actionHref: '/collaboration',
    };
}

// A member's time-boxed access is expiring soon / just expired (owner/admin view).
interface DBAccessAlert {
    id: string;
    org_name: string;
    member_email: string;
    role: string;
    access_expires_at: string;
    access_purpose: string | null;
    kind: 'expiring' | 'expired';
}
const AXP_PREFIX = 'axp:';
function accessAlertToNotification(row: DBAccessAlert): Notification {
    const asRole = INV_ROLE_LABEL[row.role] || row.role;
    const expiring = row.kind === 'expiring';
    return {
        id: `${AXP_PREFIX}${row.id}`,
        type: expiring ? 'warning' : 'info',
        title: expiring ? 'Access expiring soon' : 'Access expired',
        description: expiring
            ? `${row.member_email} (${asRole}) loses access to ${row.org_name} soon — extend or let it lapse`
            : `${row.member_email}'s access to ${row.org_name} has expired`,
        timestamp: new Date(row.access_expires_at),
        read: false,
        actionHref: '/collaboration',
    };
}

// Incoming books-access requests (a consultant asking to access YOUR org).
interface DBAccessReq {
    id: string;
    org_name: string;
    requester_name: string | null;
    requester_email: string;
    created_at: string;
}
const ACC_PREFIX = 'acc:';
function accessReqToNotification(row: DBAccessReq): Notification {
    const who = row.requester_name || row.requester_email;
    return {
        id: `${ACC_PREFIX}${row.id}`,
        type: 'warning',
        title: 'Books access requested',
        description: `${who} is requesting access to ${row.org_name}'s books — approve in Settings → Access`,
        timestamp: new Date(row.created_at),
        read: false,
        actionHref: '/settings',
    };
}

// HR reminders (em-10) — probation ending, confirmation overdue, a joiner still
// stuck in onboarding. Derived fresh from the employee rows by get_hr_reminders,
// exactly like the client-query and challan feeds above: nothing is stored, so
// nothing can go stale, and a dismissal rides notification_dismissals.
interface DBHrReminder {
    kind: 'probation_ending' | 'probation_overdue' | 'onboarding_stalled';
    employee_id: string;
    employee_name: string;
    employee_code: string;
    due_date: string;
    days_out: number;
}
const HR_PREFIX = 'hr:';
export function hrReminderToNotification(row: DBHrReminder): Notification {
    const who = `${row.employee_name} (${row.employee_code})`;
    const on = formatDate(row.due_date);
    const map = {
        probation_ending: {
            type: 'info' as const,
            title: 'Probation ending soon',
            description: `${who} finishes probation on ${on} — confirm them, or extend it.`,
        },
        probation_overdue: {
            type: 'warning' as const,
            title: 'Confirmation overdue',
            description: `${who} finished probation on ${on} and has still not been confirmed.`,
        },
        onboarding_stalled: {
            type: 'warning' as const,
            title: 'Joiner not activated',
            description: `${who} joined on ${on} but is still onboarding, so payroll will skip them.`,
        },
    }[row.kind];
    return {
        id: `${HR_PREFIX}${row.kind}:${row.employee_id}:${row.due_date}`,
        ...map,
        timestamp: new Date(row.due_date),
        read: false,
        actionHref: '/payroll',
    };
}

// ── The shared store ─────────────────────────────────────────────────
// One copy of every feed, whatever number of components are reading. The engine
// below is the only writer.

type FeedKey = 'ca' | 'doc' | 'query' | 'challan' | 'ref' | 'access' | 'invite' | 'accepted' | 'axp' | 'hr' | 'mine';
const EMPTY_FEEDS: Record<FeedKey, Notification[]> = {
    ca: [], doc: [], query: [], challan: [], ref: [], access: [], invite: [], accepted: [], axp: [], hr: [],
    // of-3: `notifications` — the per-PERSON table. Five senders have written to
    // it since 00146 (renewals, deadline reminders, the weekly risk digest,
    // books-health, bank inbox) and NOTHING in the app ever read it, because the
    // bell only knew `ca_notifications` (practice→client messaging, which needs a
    // practice_clients row and so cannot carry "a colleague gave you this task").
    mine: [],
};

/** The tables the bell's live path depends on. Each must be in the
 *  `supabase_realtime` publication or it can never push (see 20260727130844). */
export const PUSH_TABLES = [
    'ca_notifications', 'document_requests', 'access_requests',
    'invitations', 'org_members', 'referrals', 'referral_signups',
    'gst_client_packets', 'gst_filing_approvals',
    // of-3 — the per-person table. Published by 20260728131723; until that is
    // applied this name shows up in the push gap, which is exactly right: the
    // socket would otherwise look healthy and stay silent forever.
    'notifications',
];

const EMPTY_GAP: string[] = [];

interface NotifStore {
    feeds: Record<FeedKey, Notification[]>;
    loading: boolean;
    orgId: string | null;
    /** Subscribed tables that cannot push, so the UI can say why it's polling. */
    pushGap: string[];
    /** The invite feed's RAW rows — the standing Invitations card needs the
     *  token/org/status the Notification mapping flattens away. Written only by
     *  fetchInviteNotifs, so the card is live for free. */
    inviteRows: IncomingInvite[];
    setFeed: (key: FeedKey, items: Notification[]) => void;
    patchFeed: (key: FeedKey, fn: (prev: Notification[]) => Notification[]) => void;
    setLoading: (loading: boolean) => void;
    setOrgId: (orgId: string | null) => void;
    setPushGap: (tables: string[]) => void;
    setInviteRows: (rows: IncomingInvite[]) => void;
    resetOrgFeeds: () => void;
}

const useNotifStore = create<NotifStore>((set) => ({
    feeds: EMPTY_FEEDS,
    loading: true,
    orgId: null,
    pushGap: EMPTY_GAP,
    inviteRows: [],
    setFeed: (key, items) => set((s) => ({ feeds: { ...s.feeds, [key]: items } })),
    patchFeed: (key, fn) => set((s) => ({ feeds: { ...s.feeds, [key]: fn(s.feeds[key]) } })),
    setLoading: (loading) => set({ loading }),
    setOrgId: (orgId) => set({ orgId }),
    setPushGap: (tables) => set((s) => (
        s.pushGap.join() === tables.join() ? s : { pushGap: tables }
    )),
    setInviteRows: (rows) => set({ inviteRows: rows }),
    resetOrgFeeds: () => set((s) => ({ feeds: { ...s.feeds, ca: [], doc: [], query: [], challan: [], hr: [] } })),
}));

/** Invitations addressed to me, raw — for the standing Invitations card. Live:
 *  the one engine refetches them on realtime, poll and wake like every feed. */
export function useIncomingInvitations(): IncomingInvite[] {
    return useNotifStore((s) => s.inviteRows);
}

/** Subscribed tables that can't push live, if any — an empty list means all good. */
export function useRealtimePushGap(): string[] {
    return useNotifStore((s) => s.pushGap);
}

// ── The stale-response guard ─────────────────────────────────────────
// A feed's fetches OVERLAP, and their responses can come back in either order.
// Accepting an invitation writes `invitations` twice in quick succession (INSERT,
// then UPDATE to 'accepted'); the `event: '*'` binding starts a fetch for each,
// and the first one asked the database while the row was still `pending` — so its
// answer is legitimately EMPTY. When that empty answer lands second it overwrites
// the good one, the notification disappears, and the bell then sits wrong until
// the next poll rescues it.
//
// Nothing about this looks broken: the socket is healthy, the row is correct, and
// the notification does eventually arrive. It just arrives ~9.8s late instead of
// ~0.3s — measured both ways in docs/NOTIFICATION_RAILS_TEST.md Sec. 4.
//
// So: stamp every request, and let only the newest one write. An older response
// is not merged or retried — it is simply dropped, because a newer request is
// already in flight and will produce a better answer.
const feedSeq: Partial<Record<FeedKey, number>> = {};

/** Claim the newest sequence number for `key`. Call BEFORE awaiting. */
function claim(key: FeedKey): number {
    const next = (feedSeq[key] ?? 0) + 1;
    feedSeq[key] = next;
    return next;
}

/** True while `seq` is still the newest request issued for `key`. */
function freshest(key: FeedKey, seq: number): boolean {
    return feedSeq[key] === seq;
}

/**
 * Which of the currently-unread notifications deserve an alert — the single
 * decision behind every chime, toast and desktop banner, kept pure so it can be
 * tested. `seen` is MUTATED to record what has now been alerted.
 *
 * Two rules, both learned the hard way:
 *   • `primed === false` (the first load hasn't landed) → alert NOTHING, just
 *     remember. Otherwise every page open chimes the whole backlog.
 *   • an id alerts exactly ONCE, ever — no matter which transport delivered it,
 *     or how many times a refetch re-derives the same row. The chime used to live
 *     inside socket handlers, so a poll-delivered item arrived silently and a
 *     doubly-mounted hook rang twice.
 */
export function pickFreshAlerts(
    unread: Notification[],
    seen: Set<string>,
    primed: boolean,
): Notification[] {
    if (!primed) {
        unread.forEach((n) => seen.add(n.id));
        return [];
    }
    const fresh = unread.filter((n) => !seen.has(n.id));
    fresh.forEach((n) => seen.add(n.id));
    return fresh;
}

/**
 * What the batch toast SAYS. One arrival shows itself; several show the count and
 * then name what they actually are.
 *
 * It used to print `fresh[0].title` under "3 new notifications", so three
 * unrelated items read as three copies of the first one: the founder's own toast
 * said "3 new notifications / Invitation to join TIGER CABLE INDUSTRIES" and the
 * honest reading of that is "we sent you the same thing three times". Distinct
 * titles only, because two items really can share one (two "Create the challan"
 * tasks are two clients, and the count already says there are two).
 */
export function alertToastText(fresh: Notification[]): { title: string; description: string } {
    if (fresh.length === 1) {
        return { title: fresh[0].title, description: fresh[0].description };
    }
    const distinct = [...new Set(fresh.map((n) => n.title.trim()).filter(Boolean))];
    const named = distinct.slice(0, 3).join(' · ');
    return {
        title: `${fresh.length} new notifications`,
        description: distinct.length > 3 ? `${named} and ${distinct.length - 3} more` : named,
    };
}

function mergeFeeds(feeds: Record<FeedKey, Notification[]>): Notification[] {
    return Object.values(feeds).flat().sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

// ── Read side ────────────────────────────────────────────────────────

/**
 * Read the bell. Safe to call from any number of components — they all share the
 * one engine mounted by `AppLayout`, so nothing is fetched or chimed twice.
 */
export function useNotifications() {
    const feeds = useNotifStore((s) => s.feeds);
    const loading = useNotifStore((s) => s.loading);
    const orgId = useNotifStore((s) => s.orgId);

    const notifications = useMemo(() => mergeFeeds(feeds), [feeds]);
    const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

    // ── Mark as read / dismiss ──
    // Dismissing is the same race seen from the other end: a fetch issued BEFORE
    // the dismissal still has the item, and if it lands afterwards the thing you
    // just tapped away comes back. `claim()` here retires those in-flight requests
    // — anything issued after the write is newer than the dismissal and correctly
    // still wins.
    const markRead = useCallback(async (id: string) => {
        const { patchFeed, setFeed, feeds: allFeeds } = useNotifStore.getState();
        // A sticky item (a pending invitation) is an open work item: no tap, ×,
        // or stray caller may dismiss it. It clears when the thing resolves.
        if (Object.values(allFeeds).flat().find((n) => n.id === id)?.sticky) return;
        if (id.startsWith(REF_PREFIX)) {
            claim('ref');
            // Referral "seen" is tracked server-side (acknowledged_at). Persist it
            // so the unread dot doesn't return on reload. (acknowledged_at is
            // per-user all-or-nothing, so reading one clears the referral feed.)
            patchFeed('ref', (prev) => prev.map((n) => ({ ...n, read: true })));
            await supabase.rpc('acknowledge_my_referrals');
            return;
        }
        // Derived feeds (dr/acc/inv/iacc/axp/cq/ch) have no read column — persist the
        // dismissal server-side keyed by the stable id so it doesn't return on the
        // next refetch. The RPCs anti-join it back out.
        const derived: Record<string, FeedKey> = {
            [DR_PREFIX]: 'doc', [CQ_PREFIX]: 'query', [CH_PREFIX]: 'challan',
            [ACC_PREFIX]: 'access', [INV_PREFIX]: 'invite', [IACC_PREFIX]: 'accepted',
            [AXP_PREFIX]: 'axp', [HR_PREFIX]: 'hr',
        };
        const hit = Object.entries(derived).find(([prefix]) => id.startsWith(prefix));
        if (hit) {
            claim(hit[1]);
            patchFeed(hit[1], (prev) => prev.filter((n) => n.id !== id));
            await supabase.rpc('dismiss_notifications', { p_keys: [id] });
            forgetDismissals();
            return;
        }
        // of-3 — my own mail has a real `is_read` column, so it stays read.
        if (id.startsWith(MINE_PREFIX)) {
            claim('mine');
            patchFeed('mine', (prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
            await supabase.rpc('mark_my_notifications_read', { p_ids: [id.slice(MINE_PREFIX.length)] });
            return;
        }
        claim('ca');
        patchFeed('ca', (prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
        await supabase.from('ca_notifications').update({ read_at: new Date().toISOString() }).eq('id', id);
        void setFeed;
    }, []);

    // ── Mark all as read ──
    const markAllRead = useCallback(async () => {
        const state = useNotifStore.getState();
        const { feeds: f } = state;
        // Every derived notification currently shown → one bulk dismissal upsert.
        // Except sticky ones (pending invitations): "mark all read" is exactly
        // how a consultant lost every invitation their clients had sent.
        const dismissKeys = [
            ...f.doc, ...f.query, ...f.challan, ...f.access, ...f.invite, ...f.accepted, ...f.axp, ...f.hr,
        ].filter((n) => !n.sticky).map((n) => n.id);
        // Retire every in-flight fetch first, or one that is already on the wire
        // lands a moment later and un-reads the lot.
        (['ca', 'ref', 'mine', 'doc', 'query', 'challan', 'access', 'invite', 'accepted', 'axp', 'hr'] as FeedKey[])
            .forEach(claim);
        state.setFeed('ca', f.ca.map((n) => ({ ...n, read: true })));
        state.setFeed('ref', f.ref.map((n) => ({ ...n, read: true })));
        state.setFeed('mine', f.mine.map((n) => ({ ...n, read: true })));
        await supabase.rpc('mark_my_notifications_read', { p_ids: null });
        (['doc', 'query', 'challan', 'access', 'invite', 'accepted', 'axp', 'hr'] as FeedKey[])
            .forEach((k) => state.setFeed(k, f[k].filter((n) => n.sticky)));
        await supabase.rpc('acknowledge_my_referrals');
        if (dismissKeys.length) { await supabase.rpc('dismiss_notifications', { p_keys: dismissKeys }); forgetDismissals(); }
        if (state.orgId) {
            await supabase.from('ca_notifications')
                .update({ read_at: new Date().toISOString() })
                .eq('practice_id', state.orgId).is('read_at', null);
        }
    }, []);

    const refetch = useCallback(async () => { await refetchAllRef.current?.(); }, []);

    return { notifications, loading, unreadCount, markRead, markAllRead, refetch, orgId };
}

// The engine publishes its refetch here so `useNotifications().refetch()` works
// from any component without wiring a context through the tree.
const refetchAllRef: { current: (() => Promise<void>) | null } = { current: null };

/**
 * fastphone-2: the dismissal ledger, read once per refresh round. Four feeds
 * (client queries, challans, HR, document requests) each filtered by it and each
 * read it on its own: five reads of one table on a cold open. They now share one
 * read, kept for two seconds so a round's feeds all get it, and dropped the
 * moment anything is dismissed.
 */
const DISMISSAL_PREFIXES = [CQ_PREFIX, CH_PREFIX, HR_PREFIX, DR_PREFIX];
let dismissalsHeld: { at: number; keys: Promise<Set<string>> } | null = null;
function dismissedKeys(): Promise<Set<string>> {
    if (dismissalsHeld && Date.now() - dismissalsHeld.at < 2_000) return dismissalsHeld.keys;
    const toSet = (rows: unknown) => new Set(((rows as { notif_key: string }[] | null) || []).map((d) => d.notif_key));
    const keys = (async () => {
        const one = await supabase.from('notification_dismissals').select('notif_key')
            .or(DISMISSAL_PREFIXES.map((p) => `notif_key.like."${p}%"`).join(','));
        if (!one.error) return toSet(one.data);
        // The combined filter refused: read each prefix the way it always was, so
        // a dismissed card can never come back because of this.
        const each = await Promise.all(DISMISSAL_PREFIXES.map((p) =>
            supabase.from('notification_dismissals').select('notif_key').like('notif_key', `${p}%`)));
        return new Set(each.flatMap((r) => [...toSet(r.data)]));
    })();
    dismissalsHeld = { at: Date.now(), keys };
    return keys;
}
function forgetDismissals(): void { dismissalsHeld = null; }

// ── The engine — mount ONCE (AppLayout) ──────────────────────────────

/**
 * Owns every fetch, subscription, poll and alert for the bell. Mounted a single
 * time by `AppLayout`; calling it twice would double the sockets and the chimes,
 * which is exactly the bug this replaced.
 */
export function useNotificationEngine(): void {
    const { currentOrg, profile, user } = useAuth();
    const orgId = currentOrg?.id;
    const userId = profile?.id;
    const navigate = useNavigate();

    const { setFeed, setLoading, setOrgId, resetOrgFeeds } = useNotifStore.getState();

    // Latest display name for the spoken announcement, read live without
    // re-subscribing the channel.
    const nameRef = useRef('');
    nameRef.current =
        (user?.user_metadata?.full_name as string | undefined)
        || (profile as { full_name?: string } | null)?.full_name
        || user?.email?.split('@')[0]
        || '';

    useEffect(() => { setOrgId(orgId ?? null); }, [orgId, setOrgId]);

    // ── Fetchers ─────────────────────────────────────────────────────
    // Each one is idempotent and safe to call from any transport.

    const fetchQueryNotifs = useCallback(async () => {
        const seq = claim('query');
        if (!orgId) { setFeed('query', []); return; }
        const [packets, approvals, disms] = await Promise.all([
            supabase.from('gst_client_packets')
                .select('id, gstin, form, period_key, query_comment, queried_at')
                .eq('org_id', orgId).eq('status', 'queried').not('queried_at', 'is', null)
                .order('queried_at', { ascending: false }).limit(20),
            supabase.from('gst_filing_approvals')
                .select('id, query_comment, queried_at, run:gst_filing_runs!inner(org_id, gstin, return_period)')
                .eq('run.org_id', orgId).not('queried_at', 'is', null)
                .order('queried_at', { ascending: false }).limit(20),
            dismissedKeys(),
        ]);
        const dismissed = disms;
        const fromPackets: DBClientQuery[] = ((packets.data as DBClientQuery[]) || []);
        const fromApprovals: DBClientQuery[] = (((approvals.data as unknown as {
            id: string; query_comment: string | null; queried_at: string;
            run: { gstin: string | null; return_period: string | null } | null;
        }[]) || [])).map((a) => ({
            id: a.id, gstin: a.run?.gstin ?? null, form: 'GSTR-1/3B',
            period_key: a.run?.return_period ?? null,
            query_comment: a.query_comment, queried_at: a.queried_at,
        }));
        if (!freshest('query', seq)) return;
        setFeed('query', [...fromPackets, ...fromApprovals]
            .map(clientQueryToNotification)
            .filter((n) => !dismissed.has(n.id)));
    }, [orgId, setFeed]);

    const fetchChallanNotifs = useCallback(async () => {
        const seq = claim('challan');
        if (!orgId) { setFeed('challan', []); return; }
        const [tasks, disms] = await Promise.all([
            supabase.from('gst_client_packets')
                .select('id, gstin, form, period_key, approved_at, sent_at, snapshot')
                .eq('org_id', orgId).eq('status', 'awaiting_payment')
                .order('sent_at', { ascending: false }).limit(20),
            dismissedKeys(),
        ]);
        const dismissed = disms;
        if (!freshest('challan', seq)) return;
        setFeed('challan', ((tasks.data as DBChallanTask[]) || [])
            .map(challanTaskToNotification)
            .filter((n) => !dismissed.has(n.id)));
    }, [orgId, setFeed]);

    const fetchHrNotifs = useCallback(async () => {
        const seq = claim('hr');
        if (!orgId) { setFeed('hr', []); return; }
        const [res, disms] = await Promise.all([
            supabase.rpc('get_hr_reminders', { p_org_id: orgId }),
            dismissedKeys(),
        ]);
        // Payroll is a module: an org that has never switched it on has no
        // employees and the RPC may not even exist yet. Quiet is correct.
        if (res.error) { setFeed('hr', []); return; }
        const dismissed = disms;
        if (!freshest('hr', seq)) return;
        setFeed('hr', ((res.data as DBHrReminder[]) || [])
            .map(hrReminderToNotification)
            .filter((n) => !dismissed.has(n.id)));
    }, [orgId, setFeed]);

    const fetchRefNotifs = useCallback(async () => {
        const seq = claim('ref');
        const { data, error } = await supabase.rpc('get_partner_notifications');
        if (!error && freshest('ref', seq)) setFeed('ref', ((data as DBPartnerNotif[]) || []).map(refToNotification));
    }, [setFeed]);

    const fetchAccessNotifs = useCallback(async () => {
        const seq = claim('access');
        const { data, error } = await supabase.rpc('get_my_incoming_access_requests');
        if (!error && freshest('access', seq)) setFeed('access', ((data as DBAccessReq[]) || []).map(accessReqToNotification));
    }, [setFeed]);

    // These two are the pair the invitation lifecycle hammers: one INSERT and one
    // UPDATE, milliseconds apart, each firing BOTH of them. Without the guard the
    // INSERT's empty answer lands last and hides a just-accepted invitation.
    const fetchInviteNotifs = useCallback(async () => {
        const seq = claim('invite');
        const { data, error } = await supabase.rpc('get_my_incoming_invitations');
        if (!error && freshest('invite', seq)) {
            const rows = (data as IncomingInvite[]) || [];
            setFeed('invite', rows.map(inviteToNotification));
            useNotifStore.getState().setInviteRows(rows);
        }
    }, [setFeed]);

    const fetchAcceptedNotifs = useCallback(async () => {
        const seq = claim('accepted');
        const { data, error } = await supabase.rpc('get_my_accepted_invitations');
        if (!error && freshest('accepted', seq)) setFeed('accepted', ((data as DBAcceptedInvite[]) || []).map(acceptedInviteToNotification));
    }, [setFeed]);

    const fetchAccessAlertNotifs = useCallback(async () => {
        const seq = claim('axp');
        const { data, error } = await supabase.rpc('get_my_access_alerts');
        if (!error && freshest('axp', seq)) setFeed('axp', ((data as DBAccessAlert[]) || []).map(accessAlertToNotification));
    }, [setFeed]);

    // of-3 — my own mail. Silently empty until 20260728131723 is applied, so the
    // bell behaves exactly as before rather than erroring on an absent RPC.
    //
    // `mine` arrived on main while this branch was open, so it is the one feed
    // that never got the guard below. It needs it most: it is the only feed with
    // a real `is_read` column, so a stale response does not just re-show a row —
    // it writes `read: false` back over a row the user has already tapped.
    const fetchMyNotifs = useCallback(async () => {
        const seq = claim('mine');
        const { data, error } = await supabase.rpc('get_my_notifications', { p_limit: 50 });
        if (!error && freshest('mine', seq)) setFeed('mine', ((data as DBMyNotification[]) || []).map(myNotifToNotification));
    }, [setFeed]);

    const fetchOrgNotifs = useCallback(async () => {
        const caSeq = claim('ca');
        const docSeq = claim('doc');
        if (!orgId) { setFeed('ca', []); setFeed('doc', []); return; }
        const caQuery = supabase
            .from('ca_notifications')
            .select('id, type, subject, body, priority, status, created_at, read_at, practice_id, client_id')
            .eq('practice_id', orgId)
            .order('created_at', { ascending: false })
            .limit(50);

        let drQuery = supabase
            .from('document_requests')
            .select('id, org_id, title, status, requested_by, created_at')
            .eq('org_id', orgId)
            .eq('status', 'pending')
            .order('created_at', { ascending: false })
            .limit(50);
        if (userId) drQuery = drQuery.neq('requested_by', userId);

        // Doc-requests have no server read flag; their dismissals live in the
        // notification_dismissals ledger (readable by its owner via RLS).
        const dismQuery = dismissedKeys();

        const [caRes, drRes, dismRes] = await Promise.all([caQuery, drQuery, dismQuery]);
        if (!caRes.error && freshest('ca', caSeq)) setFeed('ca', (caRes.data || []).map(dbToNotification));
        if (!drRes.error && freshest('doc', docSeq)) {
            const dismissed = dismRes;
            setFeed('doc', (drRes.data as DBDocRequest[] || [])
                .map(docReqToNotification)
                .filter((n) => !dismissed.has(n.id)));
        }
    }, [orgId, userId, setFeed]);

    // ── ONE refetch path, used by every transport ────────────────────
    const inFlight = useRef<Promise<void> | null>(null);
    /**
     * Has THIS engine's own first full load landed? Not the same question as the
     * store's `loading`, which is what the alert effect used to gate on, and the
     * difference is a bug people actually hit.
     *
     * `loading` lives in the module-level store, so it flips false once per page
     * session and stays false. The engine's `seen` set, being a ref, resets on
     * every mount. So any REMOUNT met a store that said "the first load already
     * landed" with an empty memory of what had been alerted, and announced the
     * whole backlog as new. AppLayout remounts more often than it looks:
     * `/invite/$token` is a PUBLIC route parented on the root, so accepting an
     * invitation leaves the app shell and comes back into it, which is exactly
     * when the founder saw the backlog re-toast itself on 11 Aug 2026.
     *
     * This ref is per-engine, so an engine can only ever be primed by its own
     * first load, and a remount primes again from scratch.
     */
    const loadedOnce = useRef(false);
    const refetchAll = useCallback(async (): Promise<void> => {
        // Focus + visibilitychange + online can all fire together; one round trip
        // serves all of them.
        if (inFlight.current) return inFlight.current;
        const run = (async () => {
            await Promise.all([
                fetchOrgNotifs(), fetchQueryNotifs(), fetchChallanNotifs(),
                fetchRefNotifs(), fetchAccessNotifs(), fetchInviteNotifs(),
                fetchAcceptedNotifs(), fetchAccessAlertNotifs(), fetchMyNotifs(),
            ]);
            loadedOnce.current = true;
            setLoading(false);
        })().finally(() => { inFlight.current = null; });
        inFlight.current = run;
        return run;
    }, [
        fetchOrgNotifs, fetchQueryNotifs, fetchChallanNotifs, fetchRefNotifs,
        fetchAccessNotifs, fetchInviteNotifs, fetchAcceptedNotifs, fetchAccessAlertNotifs,
        fetchMyNotifs, setLoading,
    ]);

    // Publish it so any component's `refetch()` reaches this engine.
    useEffect(() => { refetchAllRef.current = refetchAll; }, [refetchAll]);

    // Identity change (sign-in, org switch) → clear org feeds and reload.
    useEffect(() => {
        if (!orgId) resetOrgFeeds();
        void refetchAll();
        void fetchHrNotifs();
    }, [orgId, userId, refetchAll, fetchHrNotifs, resetOrgFeeds]);

    // ── 1. Realtime: org-scoped ──────────────────────────────────────
    useLiveChannel(orgId ? `notifs-org:${orgId}` : null, useCallback((channel) => {
        channel
            .on('postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'ca_notifications', filter: `practice_id=eq.${orgId}` },
                (payload) => {
                    const n = dbToNotification(payload.new as DBNotification);
                    useNotifStore.getState().patchFeed('ca', (prev) => [n, ...prev.filter((p) => p.id !== n.id)].slice(0, 50));
                })
            .on('postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'ca_notifications', filter: `practice_id=eq.${orgId}` },
                (payload) => {
                    const u = dbToNotification(payload.new as DBNotification);
                    useNotifStore.getState().patchFeed('ca', (prev) => prev.map((n) => (n.id === u.id ? u : n)));
                })
            .on('postgres_changes',
                { event: '*', schema: 'public', table: 'document_requests', filter: `org_id=eq.${orgId}` },
                () => { void fetchOrgNotifs(); });
    }, [orgId, fetchOrgNotifs]), { onResubscribe: () => { void refetchAll(); } });

    // ── 2. Realtime: client queries + challan tasks ──────────────────
    // Their OWN channel, on purpose (2026-07-20 lesson): a stalled binding can take
    // a whole channel down, and these must not be able to silence the rest.
    useLiveChannel(orgId ? `notifs-queries:${orgId}` : null, useCallback((channel) => {
        channel
            .on('postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'gst_client_packets', filter: `org_id=eq.${orgId}` },
                () => { void fetchChallanNotifs(); void fetchQueryNotifs(); })
            .on('postgres_changes',
                // …or on a brief-approval link (fc-4). RLS scopes events to rows
                // this user can read, so no org filter column is needed here.
                { event: 'UPDATE', schema: 'public', table: 'gst_filing_approvals' },
                () => { void fetchQueryNotifs(); });
    }, [orgId, fetchChallanNotifs, fetchQueryNotifs]), {
        onResubscribe: () => { void fetchChallanNotifs(); void fetchQueryNotifs(); },
    });

    // ── 3. Realtime: user-scoped (cross-org) ─────────────────────────
    // Referrals, access requests, invitations, accepted invites, membership.
    // Fires regardless of which org is active — gating these on the active org
    // would hide a partner's signup bell when they have no org selected.
    useLiveChannel(userId ? `notifs-user:${userId}` : null, useCallback((channel) => {
        channel
            .on('postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'referrals', filter: `referrer_user_id=eq.${userId}` },
                () => { void fetchRefNotifs(); })
            .on('postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'referral_signups', filter: `referrer_user_id=eq.${userId}` },
                () => { void fetchRefNotifs(); })
            .on('postgres_changes',
                // RLS scopes these to rows I can see, so no explicit org filter.
                { event: '*', schema: 'public', table: 'access_requests' },
                () => { void fetchAccessNotifs(); })
            .on('postgres_changes',
                // Any invitation change I can see: addressed to my email (invitee),
                // or belonging to an org I manage. INSERT = a new invite; UPDATE =
                // accepted, cancelled, or re-sent. Both feeds refresh either way —
                // they are two cheap definer reads, and guessing which one moved is
                // how a cancelled invite used to linger in the invitee's bell.
                { event: '*', schema: 'public', table: 'invitations' },
                () => { void fetchInviteNotifs(); void fetchAcceptedNotifs(); })
            .on('postgres_changes',
                { event: '*', schema: 'public', table: 'org_members' },
                (payload) => {
                    void fetchAccessAlertNotifs();
                    // seatsChanged: a seat of MINE came or went, so the businesses
                    // I can open changed. Only my own row: a DELETE carries no
                    // user_id unless the table logs the whole row, and reading
                    // every org on somebody else's change is load for nothing.
                    const row = (payload.new ?? payload.old ?? {}) as { user_id?: string };
                    if (row.user_id && row.user_id === userId) seatsChanged();
                })
            .on('postgres_changes',
                // of-3 — my own mail. The row is already addressed to me, so the
                // filter is exact rather than relying on RLS to sort it out.
                { event: 'INSERT', schema: 'public', table: 'notifications' },
                (payload) => {
                    void fetchMyNotifs();
                    // teamscope's "You look after <client> now": the seat is already there.
                    if ((payload.new as { type?: string } | undefined)?.type === 'client_given') seatsChanged();
                });
    }, [userId, fetchRefNotifs, fetchAccessNotifs, fetchInviteNotifs, fetchAcceptedNotifs, fetchAccessAlertNotifs, fetchMyNotifs]), {
        onResubscribe: () => { void refetchAll(); },
    });

    // ── 4. Adaptive poll — the safety net under the socket ───────────
    // A socket that is UP is not the same as a socket that can deliver: a table
    // missing from the `supabase_realtime` publication never pushes, and looks
    // exactly like a healthy connection. So the interval is decided by both facts.
    //
    //   socket live AND every table we watch can push → 30s (catches only what
    //       Realtime structurally can't: rows written by cron, other schemas)
    //   socket down, connecting, OR a table can't push  → 7s
    //
    // 7 seconds is the worst case for ANY notification, with every part of the
    // live stack broken. That is the number that makes "reload to see it" gone.
    const health = useLiveHealthStore((s) => {
        const all = Object.values(s.channels);
        if (all.length === 0) return 'live' as const;
        if (all.some((h) => h === 'down')) return 'down' as const;
        return all.every((h) => h === 'live') ? 'live' as const : 'connecting' as const;
    });
    const pushGapRef = useRef(false);
    useEffect(() => {
        if (!userId) return;
        let alive = true;
        void realtimePublishedTables().then((published) => {
            // null = the probe RPC isn't applied yet → assume the socket is fine
            // (it usually is) and let the 30s sweep be the backstop.
            if (!alive || !published) return;
            const missing = PUSH_TABLES.filter((t) => !published.has(t));
            pushGapRef.current = missing.length > 0;
            if (missing.length) {
                console.warn(
                    `[notifications] these tables are not in the supabase_realtime publication, so they can never push: ${missing.join(', ')}. `
                    + 'Polling every few seconds instead — apply migration 20260727130844 to make them instant.',
                );
                useNotifStore.getState().setPushGap(missing);
            }
        });
        return () => { alive = false; };
    }, [userId]);
    const pushGap = useNotifStore((s) => s.pushGap);
    useEffect(() => {
        if (!userId) return;
        const degraded = health !== 'live' || pushGap.length > 0;
        const every = degraded ? 7_000 : 30_000;
        const id = setInterval(() => {
            // A hidden tab gains nothing from a fast poll — the wake handler below
            // refetches the instant it comes back, and browsers throttle background
            // timers to roughly one a minute anyway. Desktop alerts for a hidden
            // tab still ride the socket, which is not throttled.
            if (document.visibilityState === 'hidden' && !degraded) return;
            void refetchAll();
        }, every);
        return () => clearInterval(id);
    }, [health, pushGap, userId, refetchAll]);

    // HR reminders turn over on a calendar day — an hour is plenty, and they ride
    // the wake refetch below like everything else.
    useEffect(() => {
        if (!orgId) return;
        const id = setInterval(() => { void fetchHrNotifs(); }, 3_600_000);
        return () => clearInterval(id);
    }, [orgId, fetchHrNotifs]);

    // ── 5. Wake — focus, visible, back online ────────────────────────
    useEffect(() => {
        if (!userId) return;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const wake = () => {
            // focus + visibilitychange fire together on a tab return; coalesce.
            if (timer) return;
            timer = setTimeout(() => { timer = null; void refetchAll(); void fetchHrNotifs(); }, 150);
        };
        const onVisible = () => { if (document.visibilityState === 'visible') wake(); };
        window.addEventListener('focus', wake);
        window.addEventListener('online', wake);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener('focus', wake);
            window.removeEventListener('online', wake);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [userId, refetchAll, fetchHrNotifs]);

    // ── 6. The alert — decided HERE, once, for every feed ────────────
    // Not in the socket handlers. An item delivered by the poll, by a wake
    // refetch, or by the socket must alert identically: a notification that
    // arrives silently because of HOW it arrived is the bug this file exists to
    // never repeat.
    const feeds = useNotifStore((s) => s.feeds);
    const loading = useNotifStore((s) => s.loading);
    const seenRef = useRef<Set<string>>(new Set());
    const primedRef = useRef(false);
    useEffect(() => {
        const unread = mergeFeeds(feeds).filter((n) => !n.read);
        // `primedRef` = THIS engine has finished its own first load, so anything
        // on screen before that is pre-existing and is remembered silently. It
        // deliberately does not read the store's `loading`: that flag is global
        // and one-way, so a remounted engine inherited "already loaded" with an
        // empty `seen` and re-announced the entire backlog.
        const fresh = pickFreshAlerts(unread, seenRef.current, primedRef.current);
        // Flip AFTER the call above, so the run that first sees the whole backlog
        // is itself still silent. `loading` is the TRIGGER (it flips in the same
        // block as the last `setFeed`, giving this effect one guaranteed run once
        // everything has landed, and that is why it stays in the deps below);
        // `loadedOnce` is the AUTHORITY on whose load it was.
        if (!primedRef.current && loadedOnce.current && !loading) primedRef.current = true;
        if (fresh.length === 0) return;

        // One chime for the batch — five arrivals at once is one event to a human.
        // The sounds load when one plays; the first load carries none of them.
        const who = nameRef.current;
        void import('@/shared/utils/notificationSound').then((m) => m.alertNewNotification(who), () => undefined);

        // In front of Karr → a toast you can act on. Elsewhere → an OS banner, so
        // it reaches you the way an email does. The toast respects the pop-up
        // switch (Karr Phone → Alerts): everything still lands in the phone,
        // and the chime/desktop banner keep their own separate toggles.
        if (windowIsForeground()) {
            if (isAlertPopupsMuted()) return;
            const { title, description } = alertToastText(fresh);
            // One arrival opens ITSELF. A batch opens the full list, because the
            // toast now names three different things and a button that silently
            // picks the first of them lands somewhere the reader did not choose.
            const to = fresh.length === 1 ? fresh[0].actionHref : '/notifications';
            toast(title, {
                description,
                action: to ? { label: 'Open', onClick: () => navigate({ to }) } : undefined,
            });
        } else {
            // webpush: on a device whose phone notifications are on, my own
            // alerts already arrive by push, one per kind. A second banner from
            // this tab would be the double alert.
            const pushed = pushOnHere();
            fresh.filter((n) => !(pushed && n.id.startsWith(MINE_PREFIX))).slice(0, 3).forEach((n) => showDesktopNotification({
                id: n.id, title: n.title, body: n.description, href: n.actionHref,
            }));
        }
    }, [feeds, loading, navigate]);

    // ── 7. webpush: the icon's red number, and this phone's subscription ──
    // The count is my own unread alerts, the same count send-push puts on the
    // icon while Karr is closed, so the number does not jump when Karr opens.
    // Reading them in Alerts takes it to zero, which clears the icon.
    const mineUnread = feeds.mine.filter((n) => !n.read).length;
    useEffect(() => {
        if (!userId || !loadedOnce.current) return;
        setIconCount(mineUnread);
    }, [userId, mineUnread, loading]);
    useEffect(() => {
        if (userId) void syncPushSubscription();
    }, [userId]);
}

/**
 * webpush: a tap on a phone notification reads the rows it stood for, as a tap
 * in Alerts does. Takes the raw `notifications` ids the push carried.
 */
export async function markMyNotificationsRead(rawIds: string[]): Promise<void> {
    if (rawIds.length === 0) return;
    const keys = new Set(rawIds.map((id) => `${MINE_PREFIX}${id}`));
    claim('mine');
    useNotifStore.getState().patchFeed('mine', (prev) => prev.map((n) => (keys.has(n.id) ? { ...n, read: true } : n)));
    await supabase.rpc('mark_my_notifications_read', { p_ids: rawIds });
}

/** Current realtime health, for a UI that wants to be honest about it. */
export { useRealtimeHealth } from '@/shared/utils/liveChannel';
export { realtimeHealth };
